import {
  lighten,
  Tooltip as MuiTooltip,
  Stack,
  SvgIcon,
  styled,
  type TooltipProps,
  Typography,
  tooltipClasses,
} from '@mui/material';
import { useIsMobile } from '@side/pantry';
import { useAtomValue, useSetAtom } from 'jotai';
import { forwardRef, useCallback, useMemo } from 'react';
import {
  hoveredGroupKey,
  isGroupHoveredFamily,
} from '@/common/atoms/community';
import { MAP_PIN_POPPER_MODIFIERS } from '@/views/Community/constants';

const Tooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[3],
    width: 260,
  },
  '&[data-popper-reference-hidden]': {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
}));

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  width: 32,
  height: 32,
  path: {
    stroke: theme.palette.secondary.dark,
  },
  'path, circle': {
    fill: theme.palette.secondary.main,
  },
  '&:hover, &.active, &.selected': {
    path: {
      fill: theme.palette.secondary.main,
    },
    circle: {
      fill: theme.palette.secondary.dark,
    },
  },
  '&.selected': {
    transform: 'scale(1.25)',
    transformOrigin: 'bottom',
    // Can't use theme.shadows since it generates a syntax that is invalid for drop-shadow
    filter: 'drop-shadow(0px 3px 2px rgba(0,0,0,0.4))',
  },
  '&.outOfBounds': {
    path: {
      stroke: theme.palette.secondary.main,
    },
    'path, circle': {
      fill: lighten(theme.palette.secondary.main, 0.4),
    },
    text: {
      fill: theme.palette.secondary.dark,
    },
    '&:hover, &.active, &.selected': {
      circle: {
        fill: theme.palette.secondary.dark,
      },
      text: {
        fill: theme.palette.common.white,
      },
    },
  },
}));

interface Props {
  content: string;
  className: string;
  groupKey: string;
  address: string;
  onHover: () => unknown;
}

const popperProps = { modifiers: MAP_PIN_POPPER_MODIFIERS };

export const GroupMapPin = forwardRef<SVGSVGElement, Props>(
  ({ className, content, groupKey, address, onHover }, ref) => {
    const setHoveredGroupKey = useSetAtom(hoveredGroupKey);
    const isHovered = useAtomValue(isGroupHoveredFamily(groupKey));
    const isMobile = useIsMobile('md');

    const title = useMemo(
      () => (
        <Stack
          sx={{
            gap: 0.5,
            py: 1.5,
            px: 1,
            alignItems: 'flex-start',
          }}
        >
          <Typography variant="h5" component="span">
            {content} teams at this address
          </Typography>
          <Typography variant="body3">{address}</Typography>
        </Stack>
      ),
      [content, address],
    );

    const handleMouseEnter = useCallback(() => {
      setHoveredGroupKey(groupKey);
      onHover();
    }, [setHoveredGroupKey, groupKey, onHover]);
    const handleMouseLeave = useCallback(
      () => setHoveredGroupKey(undefined),
      [setHoveredGroupKey],
    );

    return (
      <Tooltip
        open={isHovered && !isMobile}
        placement="top"
        slotProps={{ popper: popperProps }}
        title={title}
      >
        <StyledSvgIcon
          className={className}
          data-testid="MapPinIcon"
          ref={ref}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <svg
            width="46"
            height="46"
            viewBox="0 0 46 46"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M22.9997 3.83337C15.5822 3.83337 9.58301 9.83254 9.58301 17.25C9.58301 27.3125 22.9997 42.1667 22.9997 42.1667C22.9997 42.1667 36.4163 27.3125 36.4163 17.25C36.4163 9.83254 30.4172 3.83337 22.9997 3.83337ZM22.9997 22.0417C20.3547 22.0417 18.208 19.895 18.208 17.25C18.208 14.605 20.3547 12.4584 22.9997 12.4584C25.6447 12.4584 27.7913 14.605 27.7913 17.25C27.7913 19.895 25.6447 22.0417 22.9997 22.0417Z"
              fill="#D13F44"
            />
            <circle cx="23" cy="17" r="11" />
            <circle cx="23" cy="17" r="11" fill="black" fillOpacity="0.3" />

            <text
              x="50%"
              y="40%"
              dominantBaseline="middle"
              textAnchor="middle"
              fontSize="16"
              fontWeight="700"
              fill="white"
            >
              {content}
            </text>
          </svg>
        </StyledSvgIcon>
      </Tooltip>
    );
  },
);

GroupMapPin.displayName = 'GroupMapPin';
